import { Button, FAIcon, IconTooltip, ModuleWrapper } from '@/components'
import { UserCombobox } from '@/components/combobox'
import { MonthYearPicker } from '@/components/month-year-picker'
import {
  chunkIntoWeeks,
  WorkPeriodCalendar,
  type WorkPeriodCalendarNode,
} from './WorkPeriodCalendar'
import {
  enforceHourMinuteString,
  parseFloatToTimestamp,
} from '@/modules/activities'
import { PageType } from '@/modules/common/consts'
import { Module } from '@/modules/companies'
import { endOfWeek, format, startOfWeek } from '@/utils/date-fns'
import {
  useAdmin,
  useConfirm,
  useLeaderGroups,
  useOnErrorAuto,
  useParam,
  usePermissions,
  useUser,
} from '@/utils/hooks'
import { getNodes } from '@/utils/graphql'
import {
  formatRestDuration,
  restNights,
  restTypeLabel,
  useRestLabels,
} from './components/rest-row/rest-period-format'
import { useEditDriverDiet } from './components/use-edit-driver-diet'
import { useEditSalaryDay } from './components/use-edit-salary-day'
import { RestAwardChooser } from './components/rest-row/rest-award-chooser'
import type { RestAwardKind } from './components/rest-row/rest-award-chooser'
import { useUserQuery } from '@/utils/hooks/useUser'
import { PERMISSIONS } from '@/utils/permissions'
import { cn } from '@/lib/utils'
import { useMutation, useQuery } from '@apollo/client'
import { Loader, usePrompt, useToast } from '@ur-solutions/react-components'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { AddonOverview } from './AddonOverview'
import {
  addMonths,
  compareDesc,
  eachDayOfInterval,
  endOfDay,
  endOfMonth,
  formatISO,
  isSameDay,
  isWithinInterval,
  parseISO,
  setDate,
  startOfDay,
  startOfMonth,
} from 'date-fns'
import {
  Activity,
  useCallback,
  useEffect,
  useEffectEvent,
  useMemo,
  useState,
} from 'react'
import { isMobileOnly } from 'react-device-detect'
import { useNavigate } from 'react-router'
import { useTheme } from 'styled-components'
import { CalendarColorExplanation } from './CalendarColorExplanation'
import {
  DELETE_SALARY_COMMENT_MUTATION,
  DELETE_SALARY_CORRECTION_MUTATION,
  LOCK_ALL_USER_MONTH_REPORTS,
  LOCK_CALCULATED_SALARY_FOR_USER,
} from './mutations'
import {
  LATEST_SALARY_LOCK_RUN,
  SALARY_STATISTICS,
  WORK_PERIOD_MONTH_REPORT,
  WORK_PERIODS,
  REST_PERIODS,
  WORK_PERIODS_EDITED_AFTER_LOCK,
} from './queries'
import { getApprovalRows } from './approval-rows'
import type { ApprovalRestPeriodNode } from './types.graphql'
import { UserWorkPeriodsWarningCard } from './UserWorkPeriodsWarningCard'
import {
  WORK_PERIOD_FILTER_STORAGE,
  workPeriodFilterDefaults,
  workPeriodFilterTransforms,
} from './work-period-filter-storage'
import { ViewWorkPeriodLogsModal } from './components/view-work-period-logs-modal'
import { SalaryLockLogModal } from './components/salary-lock-log-modal'
import { GroupedActivityDistributionModal } from './components/grouped-activity-distribution-modal'
import { WorkPeriodApproveTable } from './WorkPeriodApproveTable'
import { WorkPeriodApproveCards } from './WorkPeriodApproveCards'
import { WorkPeriodGroupDetails } from './WorkPeriodGroup/WorkPeriodGroupDetails'
import { WorkPeriodDetails } from './WorkPeriodGroup/WorkPeriodDetails'
import { useSalaryModuleConfiguration } from './hooks/useSalaryModuleConfiguration'
import { useWorkPeriodMigrations } from './hooks/useWorkPeriodMigrations'
import { SalaryCorrectionDialog } from './components/salary-correction-dialog'
import {
  SalaryLockModal,
  type SalaryLockChoiceMode,
} from './components/salary-lock-modal'
import { salaryLockRunToast } from './components/salary-lock-run-toast'
import { AbsenceDetailsSalary } from '../timesheets/absence-details/absence-details-salary'
import { SalaryCommentDialog } from './components/salary-comment-dialog'
import { truncate } from '@/utils/string'
import { SalaryStatistics } from './components/salary-statistics'
import { ApprovalCommentsList } from './ApprovalCommentsList'
import { getVisibleTimedeltaSums } from './utils/get-visible-timedelta-sums'
import { getScopedSalaryCodeRows } from './utils/get-scoped-salary-code-rows'
import { NotCalculatedSum } from './components/not-calculated-sum'
import type { SalarySumScope } from './salary-month-summary-cards'
import {
  getWeeklyWorkSums,
  type WeeklySumGroup,
} from './utils/get-weekly-work-sums'
import {
  formatSecondsAsHourMinute,
  getRejectedActivities,
  getRejectedActivitiesTotal,
} from './utils/get-rejected-activities'
import { DriverActivityType } from '@/modules/activities/consts'

// Shared Tailwind tokens so every panel/table on the page reads the same as the
// approval/detail components (rounded surfaces, gray borders, muted headers).
const PANEL =
  'overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm'
const SUM_TABLE = 'w-full border-collapse text-sm'
// Header chip: darker gray bar + a clear bottom divider so it reads as a header.
const THEAD = 'border-b border-gray-200 bg-gray-100'
// The side column is only 25rem, so columns sit close together to keep every
// figure column in view; the outer cells keep the panel's edge padding.
const CELL_X = 'px-2 first:pl-4 last:pr-4'
const TH = cn(
  CELL_X,
  'py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-600'
)
// Numeric header: right-aligned to sit over the figures.
const TH_NUM = cn(
  CELL_X,
  'py-3 text-right text-xs font-bold uppercase tracking-wider text-gray-600'
)
const TD = cn(CELL_X, 'py-3.5 align-top text-sm text-gray-800')
// Label column: darker + medium so each row has a clear anchor. Long salary
// code names break mid-word rather than widening the table past the panel.
const TD_NAME = cn(
  CELL_X,
  'py-3.5 align-top text-sm font-medium text-gray-900 [overflow-wrap:anywhere]'
)
// Figures: right-aligned + tabular so digits line up column-wise.
const TD_NUM = cn(
  CELL_X,
  'py-3.5 align-top text-right text-sm tabular-nums text-gray-800'
)
const SUM_ROW = 'border-t border-gray-100 transition-colors hover:bg-gray-50'
const FILTER_LABEL = 'mb-1 block text-sm font-medium text-gray-600'

interface WorkPeriodOverviewProps {}

function timeDeltaToDecimalHours(durationAsString: string | null | undefined) {
  if (!durationAsString) return 0

  const match = durationAsString.match(
    /^(-)?(?:(\d+)\s+days?,\s*)?(\d+):(\d{2})(?::(\d{2}(?:\.\d+)?))?$/
  )
  if (!match) return 0

  const sign = match[1] ? -1 : 1
  const days = Number(match[2] ?? 0)
  const hours = Number(match[3] ?? 0)
  const minutes = Number(match[4] ?? 0)
  const seconds = Number(match[5] ?? 0)
  const totalHours = sign * (days * 24 + hours + minutes / 60 + seconds / 3600)

  return Math.round(totalHours * 100) / 100
}

export const WorkPeriodOverview: React.FC<WorkPeriodOverviewProps> = () => {
  const translations = useTranslate({
    salaryCode: 'common.salary-code',
    absence: 'common.absence',
    departmentDistribution: 'salary.department-distribution-title',
    amount: 'common.amount',
    all: 'common.all',
    approved: 'common.approved',
    totalWork: 'common.total-work',
    overtimePayrollScopeOnly: 'salary.overtime-payroll-scope-only',
    activities: 'common.activities',
    workDay: 'common.work-day',
    dayRest: 'common.day-rest',
    weekRest: 'common.week-rest',
    showRest: 'common.show-rest',
    export: 'common.export',
    weekN: ['common.week-n', { n: 0 }],
    workOverview: 'salary.work-overview',
    unknownUser: 'common.unknown-user',

    sumForMonth: ['activities.sum-for-month-title', { month: '' }],
    driving: 'activities.driving',
    otherWork: 'common.other-work',
    overtime: 'common.overtime',
    undertime: 'common.undertime',
    available: 'activities.availability-time',
    transferredValue: 'salary.transferred-value',
    lock: 'salary.lock',
    salaryBase: 'common.salary-base',
    lockAll: 'salary.lock-all',
    lockAllInProgress: 'salary.lock-all-in-progress',
    lockLog: 'salary.lock-log-title',
    corrections: 'salary.corrections',
    rejectedActivities: 'salary.rejected-activities',
    estimated: 'salary.estimated',
    sum: 'common.sum',
    activityType: {
      work: 'common.work',
      availability: 'common.availability',
      paidPause: 'common.paid-pause',
      driving: 'common.driving',
      rest: 'common.rest',
      unavailable: 'common.unavailable',
    },
    nUsersEditedAfterLock: ['salary.n-users-edited-after-lock', { n: 0 }],
    oneUserEditedAfterLock: 'salary.one-user-edited-after-lock',

    toast: {
      mailSuccess: 'exports.success-email-process',
      lockBaseSuccess: 'salary.lock-success-base',
      lockDiffSuccess: 'salary.lock-success-diff',
    },
    error: {
      generic: 'error.generic-server-error',
    },

    salaryMode: 'common.salary-mode',
    month: 'common.month',
    user: 'common.user',
    view: 'common.view',
    calendar: 'common.calendar',
    list: 'common.list',

    overtimeThatOverlapsMonths: 'salary.overtime-that-overlaps-months',
    nextMonth: 'common.next-month',
    workPeriods: 'salary.work-periods',
    addSlashCorrect: 'salary.add-slash-correct',

    delete: 'common.delete-alt',
    doYouWishToDeleteSalaryCorrection:
      'salary.do-you-wish-to-delete-salary-correction',
    doYouWishToDeleteSalaryComment:
      'salary.do-you-wish-to-delete-salary-comment',
    comments: 'common.comments',
    globalCommentDescription:
      'salary.salary-comment-dialog.applies-to-all-months',
  })

  const userId = useParam('userId', true)
  const confirm = useConfirm()

  const navigate = useNavigate()

  const updateUrl = useCallback(
    (id: string | null) => {
      if (!id) return
      void navigate(`/work-periods/${id}`, { replace: true })
    },
    [navigate]
  )

  const addPrompt = usePrompt()
  const addToast = useToast()
  const openWorkPeriodLog = (workPeriodId: string) =>
    addPrompt<boolean | null>(resolve => (
      <ViewWorkPeriodLogsModal workPeriodId={workPeriodId} onClose={resolve} />
    ))

  const theme = useTheme()
  const restLabels = useRestLabels()
  const me = useUser()
  const onErrorAuto = useOnErrorAuto()
  const { hasPermissions } = usePermissions()
  const canSeeOthers = hasPermissions(
    PERMISSIONS.workPeriod.view.othersworkperiod,
    true
  )
  const canEditSalary = hasPermissions(
    PERMISSIONS.salary.change.salarybase,
    true
  )
  const canSeeSalaryStatistics = hasPermissions(
    PERMISSIONS.salary.view.salarybaseentry,
    true
  )
  const isAdmin = useAdmin()

  const salaryConfiguration = useSalaryModuleConfiguration()
  const hasApprovalFlow =
    salaryConfiguration.driverApprove || salaryConfiguration.managerApprove
  const payrollSumScope: SalarySumScope =
    salaryConfiguration.onlyIncludeApprovedHours ? 'approved' : 'all'

  const [activeSalaryCode, setActiveSalaryCode] = useState<string | null>(null)
  const [filterStorageStateLoading, setFilterStorageStateLoading] =
    useState(true)
  const [popupWorkPeriodGroupId, setPopupWorkPeriodGroupId] = useState<
    string | null
  >(null)

  const {
    formValues: filter,
    updateForm,
    formChangeHandler: handler,
    loadStoredFormState,
    updateInitialValues,
  } = useForm({
    values: workPeriodFilterDefaults({ user: userId ?? null }),
    config: {
      callbacks: {
        user: updateUrl,
      },
      storage: {
        storeFormState: true,
        sessionStorage: true,
        storeFormStateName: WORK_PERIOD_FILTER_STORAGE,
        transformValues: workPeriodFilterTransforms(canSeeOthers),
      },
    },
  })

  const { user: filterUser, loading: userLoading } = useUserQuery(
    userId,
    Module.ACTIVITIES
  )

  const [startDateCalculation, endDateCalculation] = useMemo((): [
    Date,
    Date,
  ] => {
    // If salary view is active, we need to set interval according to salary start and end dates.
    if (filterUser && filterUser.salaryWorkTime) {
      const baseDate = startOfDay(filter.month)
      const salaryStart = filterUser.salaryWorkTime?.salaryDateStart ?? 1
      const salaryEnd = filterUser.salaryWorkTime?.salaryDateEnd ?? 1

      const forward = 31 - salaryStart > 15

      // What salary period is connected to which month is based on the salary start date.
      if (forward) {
        const startMonthYearTime = setDate(baseDate, salaryStart)
        const endMonthYearTime =
          salaryStart === 1
            ? endOfMonth(baseDate)
            : setDate(addMonths(baseDate, 1), salaryEnd)

        return [startMonthYearTime, endOfDay(endMonthYearTime)]
      }

      const startMonthYearTime = setDate(addMonths(baseDate, -1), salaryStart)
      const endMonthYearTime = setDate(baseDate, salaryEnd)

      return [startMonthYearTime, endOfDay(endMonthYearTime)]
    } else {
      // If salary view is not active, we need to set interval according to the month.
      const startMonthYearTime = startOfMonth(filter.month)
      const endMonthYearTime = endOfMonth(filter.month)

      return [startMonthYearTime, endMonthYearTime]
    }
  }, [filter.month, filterUser])

  const { data, loading, refetch } = useQuery(WORK_PERIOD_MONTH_REPORT, {
    skip: filterStorageStateLoading || userLoading,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    variables: {
      user: userId ?? me.id,
      datetimeStart: formatISO(startDateCalculation),
      datetimeEnd: formatISO(endDateCalculation),
      year: filter.month.getFullYear(),
      month: filter.month.getMonth() + 1,
    },
    onError: onErrorAuto(),
  })

  // The salary code table totals each code for the month. This unrolls the same
  // hours into the activity blocks behind them, each with the department and
  // facility it is attributed to, so an attribution that looks wrong can be
  // traced to the day and the vehicle that produced it.
  const openSalaryCodeDistribution = () =>
    addPrompt<boolean | null>(resolve => (
      <GroupedActivityDistributionModal
        workPeriodGroups={data?.monthReport.workPeriodGroups ?? []}
        onClose={resolve}
      />
    ))

  const showList = filter.viewMode === 'list'

  const {
    data: listData,
    loading: listLoading,
    refetch: refetchList,
  } = useQuery(WORK_PERIODS, {
    skip: filterStorageStateLoading || userLoading || !showList,
    fetchPolicy: 'cache-and-network',
    variables: {
      user: userId ?? me.id,
      datetimeStart: formatISO(startDateCalculation),
      datetimeEnd: formatISO(endDateCalculation),
    },
    onError: onErrorAuto(),
  })

  const { data: restData, refetch: refetchRests } = useQuery(REST_PERIODS, {
    skip: filterStorageStateLoading || userLoading,
    fetchPolicy: 'cache-and-network',
    variables: {
      user: userId ?? me.id,
      datetimeStart: formatISO(startDateCalculation),
      datetimeEnd: formatISO(endDateCalculation),
      weekRestsOnly: true,
    },
    onError: onErrorAuto(),
  })

  const { data: editedAfterLockData } = useQuery(
    WORK_PERIODS_EDITED_AFTER_LOCK,
    {
      skip: filterStorageStateLoading || userLoading || !canEditSalary,
      fetchPolicy: 'cache-and-network',
      variables: {
        datetimeStart: formatISO(startDateCalculation),
        datetimeEnd: formatISO(endDateCalculation),
      },
      onError: onErrorAuto(),
    }
  )

  const editedAfterLockUsers = useMemo(
    () =>
      (editedAfterLockData?.workPeriodsEditedAfterLock ?? []).filter(
        entry => entry.user.includeInSalaryCalculation
      ),
    [editedAfterLockData?.workPeriodsEditedAfterLock]
  )

  const { myLeaderGroups } = useLeaderGroups()

  const weekRests = useMemo(
    () => getNodes(restData?.allRestPeriods),
    [restData]
  )

  // The list view is the approval table, so a week rest earns a row here for
  // the same reason it does there: it is what the diets and salary days hang
  // on. Daily rests are left out -- this view has no filter to switch them off.
  const listApprovalRows = useMemo(
    () =>
      getApprovalRows(
        getNodes(listData?.allWorkPeriods),
        getNodes(listData?.allCalendarItems),
        weekRests
      ),
    [listData, weekRests]
  )

  const refetchListAndReport = () => {
    void refetch()
    if (showList) void refetchList()
  }

  const { patchWorkPeriod } = useWorkPeriodMigrations()

  const [lockSalaryForUser, { loading: lockLoading }] = useMutation(
    LOCK_CALCULATED_SALARY_FOR_USER,
    {
      onError: onErrorAuto(),
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: WORK_PERIOD_MONTH_REPORT,
          variables: {
            user: userId ?? me.id,
            datetimeStart: formatISO(startDateCalculation),
            datetimeEnd: formatISO(endDateCalculation),
            year: filter.month.getFullYear(),
            month: filter.month.getMonth() + 1,
          },
        },
        ...(canSeeSalaryStatistics
          ? [
              {
                query: SALARY_STATISTICS,
                variables: {
                  year: filter.month.getFullYear(),
                  month: filter.month.getMonth() + 1,
                },
              },
            ]
          : []),
      ],
    }
  )

  const handleLock = async () => {
    if (!filter.user) return

    // Explicit intent so the backend can reject mismatches (e.g. creating a
    // base when one exists) rather than silently overwriting. With no base yet
    // → CREATE (no choice); with a base → the user picks RECREATE or DIFFERENCE.
    let mode: SalaryLockChoiceMode | 'CREATE' = 'CREATE'
    if (data?.monthReport?.isLocked) {
      const { data: choice } = await addPrompt<{
        mode: SalaryLockChoiceMode
      } | null>(resolve => <SalaryLockModal onResolve={resolve} />)
      if (!choice) return
      mode = choice.mode
    }

    try {
      const result = await lockSalaryForUser({
        variables: {
          user: filter.user,
          datetimeStart: formatISO(startDateCalculation),
          datetimeEnd: formatISO(endDateCalculation),
          mode,
          // The salary month this period is filed under. For salary start days
          // >= 16 the period starts in the previous calendar month, so the
          // backend must be told the grouping month explicitly (see
          // startDateCalculation) rather than derive it from datetimeStart.
          year: filter.month.getFullYear(),
          month: filter.month.getMonth() + 1,
        },
      })
      if (result.data?.lockUserMonthReport?.success) {
        addToast(
          'success',
          mode === 'DIFFERENCE'
            ? translations.toast.lockDiffSuccess
            : translations.toast.lockBaseSuccess
        )
      }
    } catch {
      // Errors are surfaced via the mutation's onErrorAuto handler.
    }
  }

  // A "lock all" run outlives the page, so a still-running one is picked back up
  // on mount (and after a reload) to keep the button disabled and the progress
  // toast alive.
  const {
    data: lockRunData,
    refetch: refetchLatestLockRun,
    startPolling: startPollingLockRun,
    stopPolling: stopPollingLockRun,
  } = useQuery(LATEST_SALARY_LOCK_RUN, {
    skip: !isAdmin || !canEditSalary,
    fetchPolicy: 'network-only',
    variables: {
      year: filter.month.getFullYear(),
      month: filter.month.getMonth() + 1,
    },
    onError: onErrorAuto(),
  })
  const latestLockRun = lockRunData?.latestSalaryLockRun
  const lockRunInProgress =
    latestLockRun?.status === 'PENDING' || latestLockRun?.status === 'RUNNING'
  const runningLockRunId = lockRunInProgress ? latestLockRun.id : null

  // Poll only while a run is live: this query is what re-enables the button
  // once the run finishes, and the toast (which polls the run itself) has no way
  // to tell the page it is done.
  useEffect(() => {
    if (!runningLockRunId) {
      stopPollingLockRun()
      return
    }
    salaryLockRunToast(runningLockRunId)
    startPollingLockRun(3000)
    return () => stopPollingLockRun()
  }, [runningLockRunId, startPollingLockRun, stopPollingLockRun])

  const [lockAllUserMonthReports, { loading: lockAllLoading }] = useMutation(
    LOCK_ALL_USER_MONTH_REPORTS,
    { onError: onErrorAuto() }
  )

  const handleLockAll = async () => {
    const { data: choice } = await addPrompt<{
      mode: SalaryLockChoiceMode
    } | null>(resolve => <SalaryLockModal allUsers onResolve={resolve} />)
    if (!choice) return

    try {
      const result = await lockAllUserMonthReports({
        variables: {
          year: filter.month.getFullYear(),
          month: filter.month.getMonth() + 1,
          mode: choice.mode,
        },
      })
      const runId = result.data?.lockAllUserMonthReports?.salaryLockRun?.id
      if (runId) {
        salaryLockRunToast(runId)
        void refetchLatestLockRun()
      }
    } catch {
      // Errors are surfaced via the mutation's onErrorAuto handler.
    }
  }

  const [deleteSalaryCorrection] = useMutation(
    DELETE_SALARY_CORRECTION_MUTATION
  )
  const [deleteSalaryComment] = useMutation(DELETE_SALARY_COMMENT_MUTATION)

  const allDates = eachDayOfInterval({
    start: startOfWeek(startDateCalculation),
    end: endOfWeek(endDateCalculation),
  })

  const popupWorkPeriodGroup = useMemo(() => {
    return (
      data?.monthReport?.workPeriodGroups.find(
        group => group.id === popupWorkPeriodGroupId
      ) ?? null
    )
  }, [data, popupWorkPeriodGroupId])

  const handleSetFilterStorageStateLoading = useEffectEvent(
    (loading: boolean) => {
      setFilterStorageStateLoading(loading)
    }
  )

  useEffect(() => {
    /* Ensuring the query for ActivityReport is only run once on render. */
    if (filterStorageStateLoading) {
      const loadedFormData = loadStoredFormState()
      if (loadedFormData) {
        // The URL user wins when present; otherwise fall back to the user
        // cached from either view. Land on that user's URL so the
        // (URL-keyed) report queries pick it up.
        const resolvedUser = userId ?? loadedFormData.user ?? null
        updateInitialValues({ ...loadedFormData, user: resolvedUser })
        if (!userId && resolvedUser) updateUrl(resolvedUser)
      }

      handleSetFilterStorageStateLoading(false)
    }
  }, [
    filter.month,
    filterStorageStateLoading,
    updateInitialValues,
    loadStoredFormState,
    userId,
    updateUrl,
  ])

  const workPeriodGroups = data?.monthReport?.workPeriodGroups

  // Salary-basis lock history for the selected user/month (ordered newest-first
  // by the backend), shown in a prompt from the button next to "Lås".
  const lockLogs = data?.monthReport?.lockLogs ?? []
  const openSalaryLockLog = () =>
    addPrompt<boolean | null>(resolve => (
      <SalaryLockLogModal
        logs={lockLogs}
        salaryBaseExists={Boolean(data?.monthReport?.isLocked)}
        onClose={resolve}
      />
    ))

  const usesPlannerForOvertime = useMemo(
    () =>
      workPeriodGroups?.some(group =>
        group.workPeriods.some(wp => wp.salarySetting?.usePlannerForOvertime)
      ) ?? false,
    [workPeriodGroups]
  )

  // A weekly rest is the stretch away from home that the diets and salary days
  // hang off, so the month view has to show where those stretches are. Daily
  // rests are left out here: there is one after every shift, and a month of
  // them would bury the work they sit between.
  // A work day opens into its own detail view. A rest carries one editable
  // thing here, so the bar opens that form directly rather than a view built
  // around a single field.
  const onRestAwardChanged = () => {
    void refetchRests()
    void refetch()
  }
  const editDriverDiet = useEditDriverDiet(
    filter.user ?? me.id,
    onRestAwardChanged
  )
  const editSalaryDay = useEditSalaryDay(
    filter.user ?? me.id,
    onRestAwardChanged
  )

  const editRestAward = async (
    restPeriod: ApprovalRestPeriodNode,
    kind: RestAwardKind
  ) => {
    const maxPartOfDay = restNights(
      parseISO(restPeriod.datetimeStart),
      parseISO(restPeriod.datetimeEnd)
    )
    if (kind === 'diet')
      return editDriverDiet({
        restPeriodId: restPeriod.id,
        maxPartOfDay,
        driverDiet: restPeriod.driverDiet,
      })
    return editSalaryDay({
      restPeriodId: restPeriod.id,
      maxPartOfDay,
      salaryDay: restPeriod.salaryDay,
    })
  }

  // Always opens, whether or not the rest can still be changed. A calendar bar
  // looks clickable either way, so skipping the handler on a locked rest --
  // which is most of them once a month is approved -- reads as the feature
  // being broken rather than as the rest being settled.
  const openRestAward = (restPeriod: ApprovalRestPeriodNode) => async () => {
    const canEdit =
      canEditSalary && !restPeriod.workPeriodBefore.approvedByManager

    const { data: kind } = await addPrompt<RestAwardKind | null>(resolve => (
      <RestAwardChooser
        restPeriod={restPeriod}
        title={restTypeLabel(restPeriod.restType, restLabels)}
        canEdit={canEdit}
        onChoose={resolve}
      />
    ))
    if (!kind) return
    await editRestAward(restPeriod, kind)
  }

  // Hovering a salary code in the month table narrows every bar to what it
  // contributes to that code. A rest is where the diets and salary days
  // actually hang, so it has to answer the same question as a work day: the
  // amount when it carries that code, and nothing at all when it does not.
  const restAwardForActiveCode = (restPeriod: ApprovalRestPeriodNode) =>
    [restPeriod.driverDiet, restPeriod.salaryDay].find(
      award =>
        award &&
        award.salaryCode?.name === activeSalaryCode &&
        (award.partOfDay ?? 0) > 0
    )

  const weekRestNodesForDay = (date: Date): WorkPeriodCalendarNode[] =>
    weekRests
      .filter(restPeriod =>
        isWithinInterval(date, {
          start: startOfDay(parseISO(restPeriod.datetimeStart)),
          end: endOfDay(parseISO(restPeriod.datetimeEnd)),
        })
      )
      .flatMap(restPeriod => {
        const award = activeSalaryCode
          ? restAwardForActiveCode(restPeriod)
          : undefined
        if (activeSalaryCode && !award) return []

        return [
          {
            id: restPeriod.id,
            text: award
              ? String(award.partOfDay)
              : `${restTypeLabel(
                  restPeriod.restType,
                  restLabels
                )} ${formatRestDuration(
                  parseISO(restPeriod.datetimeStart),
                  parseISO(restPeriod.datetimeEnd)
                )}`,
            // Blue is the rest's own mark. It stays on the icon rather than
            // the bar: the bar colours are the approval scale, and a rest has
            // no approval state of its own to place on it.
            color: theme.colors.primary500,
            variant: 'weekRest' as const,
            onClick: openRestAward(restPeriod),
          },
        ]
      })

  const calendarData = allDates.map(date => ({
    date: date,
    weekNumber: date.getDay() === 1 ? format(date, 'w') : null,
    grayed: !isWithinInterval(date, {
      start: startDateCalculation,
      end: endDateCalculation,
    }),
    nodes: [
      ...weekRestNodesForDay(date),
      ...(workPeriodGroups
        ?.filter(group =>
          activeSalaryCode
            ? group.groupedActivitySums.filter(
                sum => sum.name === activeSalaryCode && sum.sum !== '00:00'
              ).length > 0 ||
              group.groupedActivitySumsFloat.filter(
                sum => sum.name === activeSalaryCode && sum.sum !== 0
              ).length > 0
            : true
        )
        .reduce<WorkPeriodCalendarNode[]>((acc, cur) => {
          const approvedByDriver =
            cur.approvedByUser || !salaryConfiguration.driverApprove
          const approvedByManager =
            cur.approvedByManager || !salaryConfiguration.managerApprove

          if (
            salaryConfiguration.onlyIncludeApprovedHours &&
            (!approvedByDriver || !approvedByManager) &&
            activeSalaryCode &&
            // Calendar-item-only groups (e.g. empty overtime items) have no work
            // period to approve and are counted unconditionally in the summary, so
            // the approval gate must not hide them on hover.
            cur.workPeriods.length !== 0
          ) {
            return acc
          }
          const entryRange = cur.calendarItem
            ? {
                start: parseISO(cur.calendarItem.start),
                end: parseISO(cur.calendarItem.end),
              }
            : cur.workPeriods?.[0]
            ? {
                start: parseISO(cur.workPeriods[0]?.datetimeStart),
                end: parseISO(cur.workPeriods[0]?.datetimeEnd),
              }
            : cur.absenceDay
            ? {
                start: startOfDay(parseISO(cur.absenceDay.date)),
                end: endOfDay(parseISO(cur.absenceDay.date)),
              }
            : null
          if (!entryRange) return acc
          const startingThisDay = isSameDay(date, entryRange.start)
          const endingThisDay = isSameDay(date, entryRange.end)
          const isInRange =
            isWithinInterval(date, {
              start: entryRange.start,
              end: entryRange.end,
            }) ||
            startingThisDay ||
            endingThisDay
          if (isInRange) {
            // Use the month-scoped sums so a shift crossing midnight into a new
            // month shows the current month's contribution on hover, not the
            // whole shift (groupedActivitySums stays the full shift).
            const activeSalaryCodeSum = cur?.monthGroupedActivitySums.find(
              activitySum => activitySum.name === activeSalaryCode
            )?.sum
            const activeSalaryCodeSumFloat =
              cur?.groupedActivitySumsFloat?.find(
                activitySum => activitySum.name === activeSalaryCode
              )?.sum
            const calendarText = cur.calendarItem?.title ?? ''
            let calendarTimeText = ''
            if (activeSalaryCode && activeSalaryCodeSum) {
              calendarTimeText = enforceHourMinuteString(activeSalaryCodeSum)
            } else if (activeSalaryCode && activeSalaryCodeSumFloat) {
              calendarTimeText = activeSalaryCodeSumFloat.toString()
            } else {
              calendarTimeText = enforceHourMinuteString(
                cur.totalWorkTime ?? ''
              )
            }
            const text =
              calendarText && calendarTimeText && !activeSalaryCode
                ? `${calendarText}: (${calendarTimeText})`
                : cur.absenceDay
                ? `${calendarTimeText}: ${
                    cur.absenceDay.absence?.absenceType?.name ??
                    translations.absence
                  }`
                : calendarTimeText ?? ''
            let color = theme.colors.green

            if (
              (!approvedByDriver || !approvedByManager) &&
              cur.workPeriods.length !== 0
            ) {
              color = theme.colors.orange
            }
            if (
              !cur.calendarItem &&
              usesPlannerForOvertime &&
              cur.workPeriods.length !== 0
            ) {
              color = theme.colors.red8
            }
            if (cur.absenceDay) {
              color = theme.colors.blue400
            }
            // Edited-after-lock is shown via the bar icon, not the dot color.
            const editedWorkPeriod = cur.workPeriods.find(
              wp => wp.editedAfterLock
            )
            acc.push({
              // Group id so the calendar can join the same period across the
              // days it spans (and keep distinct absence days separate).
              id: cur.id,
              text: text,
              color: color,
              hasManualHours: cur.workPeriods.some(
                wp => wp.timeEntries.length > 0
              ),
              editedAfterLock: !!editedWorkPeriod,
              onLogClick: editedWorkPeriod
                ? () => openWorkPeriodLog(editedWorkPeriod.id)
                : undefined,
              onClick:
                cur.absenceDay && !cur.absenceDay.absence
                  ? undefined
                  : () => setPopupWorkPeriodGroupId(cur.id),
            })
          }
          return acc
        }, []) ?? []),
    ],
  }))

  // Per-week aggregated work sums for the calendar's info icons, aligned by
  // index with the grid's week rows (same `chunkIntoWeeks` the grid uses).
  // A group spanning a week boundary draws a bar in both rows and is summed in
  // both too, for the hours it worked in each: the backend splits its hours on
  // the week boundary (`weekSums`), so each group is still counted exactly once
  // across the month.
  const weekIntervals = chunkIntoWeeks(allDates).reduce<
    { start: Date; end: Date }[]
  >((acc, chunk) => {
    const first = chunk[0]
    const last = chunk[chunk.length - 1]
    if (first && last)
      acc.push({ start: startOfDay(first), end: endOfDay(last) })
    return acc
  }, [])

  // These sums cover the week's paid work: unlike the bars they are not
  // narrowed by the `activeSalaryCode` highlight, but like the month table
  // below them they leave out what the month does not pay — the backend omits
  // work periods an approval flow has not signed off and the activities
  // rejected from the salary calculation, so the weeks add up to that table.
  // The figures are month-clipped for the same reason it is: a shift crossing
  // the month boundary reports only this month's share.
  //
  // The total is deliberately NOT the sum of those rows. Addon and overtime
  // codes are emitted as their own lines over the same hours (a Diett line
  // shares its exact start/end with the work line it accompanies), so the rows
  // can add up to double the hours worked. `totalWorkTime` counts activity
  // duration once — equal to the union of the lines' time spans — and is the
  // only figure here that means hours worked.
  const weeklySums = getWeeklyWorkSums(
    (workPeriodGroups ?? []).map<WeeklySumGroup>(group => ({
      // Each entry is one ISO week's share of this group, so a shift crossing
      // Monday midnight is counted in both weeks for the hours worked in
      // each. The Monday it is keyed by places it in its own week row.
      weekSums: group.weekSums.map(weekSum => ({
        date: parseISO(weekSum.weekStart),
        totalWorkTime: weekSum.totalWorkTime,
        groupedActivitySums: weekSum.groupedActivitySums,
      })),
    })),
    weekIntervals
  )

  const hasOverlappingMonths = data?.monthReport?.overtime?.some(
    activitySum => activitySum.nextMonth
  )

  const overtimeText = (
    <p className="text-sm text-gray-600">
      * {translations.overtimeThatOverlapsMonths}{' '}
      <button
        type="button"
        className="text-primary underline hover:no-underline"
        onClick={() => updateForm({ month: addMonths(filter.month, 1) })}
      >
        {translations.nextMonth.toLowerCase()}
      </button>
    </p>
  )

  async function handlePatchWorkPeriod(
    workPeriodId: string,
    values: {
      approvedByUser?: boolean
      approvedByManager?: boolean
      calendarItem?: string | null
      salarySetting?: string | null
    }
  ) {
    const { data } = await patchWorkPeriod({
      variables: {
        id: workPeriodId,
        input: values,
      },
    })
    // Changing the salary work time recomputes sums/overtime, so reload the
    // month report to keep the detail view and summaries in sync.
    if ('salarySetting' in values) {
      void refetch()
    }
    if (data?.patchWorkPeriod.workPeriod && popupWorkPeriodGroup) {
      const newPopupWorkPeriodGroup = {
        ...popupWorkPeriodGroup,
        workPeriods: popupWorkPeriodGroup.workPeriods.map(workPeriod => {
          if (workPeriod.id === workPeriodId) {
            return {
              ...workPeriod,
              ...data.patchWorkPeriod.workPeriod,
            }
          }
          return workPeriod
        }),
      }
      if ('calendarItem' in values) {
        setPopupWorkPeriodGroupId(null)
      } else {
        setPopupWorkPeriodGroupId(newPopupWorkPeriodGroup.id)
      }
    }
  }

  function handleExitDetails() {
    setPopupWorkPeriodGroupId(null)
  }

  const timedeltaRows = useMemo(() => {
    const visibleRows = getVisibleTimedeltaSums(
      data?.monthReport?.groupedActivitySumsAll ?? [],
      data?.monthReport?.overtime ?? [],
      data?.salaryBaseByMonth?.entriesWithoutDepartment ?? [],
      Boolean(data?.monthReport?.isLocked)
    )

    return getScopedSalaryCodeRows(
      visibleRows,
      data?.monthReport?.groupedActivitySumsApproved ?? [],
      data?.monthReport?.payrollScopedSalaryIds ?? [],
      payrollSumScope
    ).map(activitySum => {
      const lockedValues =
        data?.salaryBaseByMonth?.entriesWithoutDepartment.filter(entry => {
          const local = entry?.salaryId
          const el = activitySum.salaryId
          return local === el
        })
      const lockedValue = lockedValues?.length
        ? parseFloatToTimestamp(lockedValues[0]?.amount ?? 0) ?? '00:00'
        : '00:00'

      return (
        <tr
          key={`td-${filter.user ?? ''}${activitySum.id}`}
          className={SUM_ROW}
          onMouseEnter={() => setActiveSalaryCode(activitySum.name)}
        >
          <td className={TD_NAME}>{activitySum.name}</td>
          <td className={TD_NUM}>
            {activitySum.notCalculated === 'all' ? (
              <NotCalculatedSum
                reason={translations.overtimePayrollScopeOnly}
              />
            ) : (
              enforceHourMinuteString(activitySum.all ?? '')
            )}
          </td>
          {hasApprovalFlow && (
            <td className={TD_NUM}>
              {activitySum.notCalculated === 'approved' ? (
                <NotCalculatedSum
                  reason={translations.overtimePayrollScopeOnly}
                />
              ) : (
                enforceHourMinuteString(activitySum.approved ?? '00:00')
              )}
            </td>
          )}
          <td className={TD_NUM}>
            {data?.monthReport.isLocked ? lockedValue : ''}
          </td>
        </tr>
      )
    })
  }, [
    data?.monthReport,
    data?.salaryBaseByMonth?.entriesWithoutDepartment,
    filter.user,
    hasApprovalFlow,
    payrollSumScope,
    setActiveSalaryCode,
    translations.overtimePayrollScopeOnly,
  ])

  async function handleDeleteCorrection(salaryCorrectionId: string) {
    const { data } = await confirm(
      '',
      translations.doYouWishToDeleteSalaryCorrection
    )
    if (!data) return
    await deleteSalaryCorrection({
      variables: {
        id: salaryCorrectionId,
      },
    })
    await refetch()
  }

  const correctionRows =
    data?.monthReport?.salaryCorrections?.map(correction => {
      return (
        <tr
          key={`corr-${correction.id}`}
          className={SUM_ROW}
          onMouseEnter={() => setActiveSalaryCode(correction.salaryCode.name)}
        >
          <td className={TD}>
            <div className="flex items-center justify-between gap-2">
              <div className="w-full min-w-0">
                <div
                  className="overflow-hidden text-ellipsis"
                  title={correction.salaryCode.name}
                >
                  {truncate(correction.salaryCode.name, 20)}
                </div>
                {correction.reason && (
                  <div className="text-sm break-words whitespace-pre-wrap text-muted-foreground">
                    {correction.reason}
                  </div>
                )}
              </div>
              <div className="flex shrink-0 gap-1">
                <IconTooltip
                  icon={{
                    icon: 'user-circle',
                    type: 'regular',
                    style: { color: theme.colors.primary },
                  }}
                >
                  {correction.createdBy
                    ? correction.createdBy.fullName
                    : translations.unknownUser}
                </IconTooltip>
                <IconTooltip
                  icon={{
                    icon: 'clock',
                    type: 'regular',
                    style: { color: theme.colors.primary },
                  }}
                >
                  {format(parseISO(correction.createdAt), 'dd.MM.yyyy HH:mm')}
                </IconTooltip>
              </div>
            </div>
          </td>
          <td className={TD_NUM}>{correction.amount}</td>
          <td className={cn(TD, 'text-right')}>
            <div className="flex items-center justify-end gap-2">
              {canEditSalary && !loading && (
                <SalaryCorrectionDialog
                  correction={correction}
                  userId={filter.user ?? me.id}
                  year={filter.month.getFullYear()}
                  month={filter.month.getMonth() + 1}
                  onSubmit={() => refetch()}
                />
              )}
              {canEditSalary && (
                <Button
                  size="sm"
                  onClick={() => handleDeleteCorrection(correction.id)}
                >
                  <FAIcon icon="times" />
                </Button>
              )}
            </div>
          </td>
        </tr>
      )
    }) ?? []

  const rejectedActivities = useMemo(
    () =>
      getRejectedActivities(workPeriodGroups ?? [], {
        start: startDateCalculation,
        end: endDateCalculation,
      }),
    [workPeriodGroups, startDateCalculation, endDateCalculation]
  )

  const activityTypeLabels: Record<string, string> = {
    [DriverActivityType.WORK]: translations.activityType.work,
    [DriverActivityType.AVAILABLE]: translations.activityType.availability,
    [DriverActivityType.PAID_REST]: translations.activityType.paidPause,
    [DriverActivityType.DRIVE]: translations.activityType.driving,
    [DriverActivityType.REST]: translations.activityType.rest,
    [DriverActivityType.NOT_AVAILABLE]: translations.activityType.unavailable,
  }

  const rejectedActivityRows = rejectedActivities.map(rejected => (
    <tr
      key={`rejected-${rejected.isEstimate ? 'estimate' : 'activity'}-${
        rejected.id
      }`}
      className={SUM_ROW}
    >
      <td className={TD}>
        <div className="flex flex-col">
          <span className="font-medium text-gray-900">
            {format(rejected.start, 'dd.MM.yyyy')}
            {' - '}
            {activityTypeLabels[rejected.activityType] ?? rejected.activityType}
          </span>
          <span className="text-xs text-gray-500">
            {rejected.spans
              .map(
                span =>
                  `${format(span.start, 'HH:mm')}-${format(span.end, 'HH:mm')}`
              )
              .join(', ')}
            {rejected.isEstimate && ` (${translations.estimated})`}
          </span>
          {!!rejected.comment && (
            <span
              className="mt-1 overflow-hidden text-ellipsis text-xs text-gray-500"
              title={rejected.comment}
            >
              {truncate(rejected.comment, 40)}
            </span>
          )}
        </div>
      </td>
      <td className={TD_NUM}>{formatSecondsAsHourMinute(rejected.seconds)}</td>
      <td className={TD} />
    </tr>
  ))

  async function handleDeleteComment(salaryCommentId: string) {
    const { data } = await confirm(
      '',
      translations.doYouWishToDeleteSalaryComment
    )
    if (!data) return
    void deleteSalaryComment({
      variables: {
        id: salaryCommentId,
      },
    })
    await refetch()
  }

  const workPeriodsInScope = workPeriodGroups?.reduce(
    (acc, group) => {
      return [...acc, ...group.workPeriods]
    },
    [] as (typeof workPeriodGroups)[number]['workPeriods'][number][]
  )

  const salaryComments = useMemo(
    () =>
      (data?.allSalaryComments ?? [])
        .map(comment => ({ comment, isGlobal: Boolean(comment.isGlobal) }))
        .sort((left, right) => {
          if (left.isGlobal !== right.isGlobal) return left.isGlobal ? -1 : 1

          return compareDesc(
            parseISO(left.comment.createdAt),
            parseISO(right.comment.createdAt)
          )
        }),
    [data?.allSalaryComments]
  )

  const commentRows =
    salaryComments.map(({ comment, isGlobal }) => {
      return (
        <tr
          key={`comment-${filter.user ?? ''}${comment.id}`}
          className={SUM_ROW}
        >
          <td className={TD} colSpan={2}>
            <div className="flex items-center justify-between gap-2">
              <div className="w-full" title={comment.value}>
                <div className="overflow-hidden text-ellipsis">
                  {truncate(comment.value, 20)}
                </div>
                {isGlobal ? (
                  <div className="text-sm text-muted-foreground">
                    {translations.globalCommentDescription}
                  </div>
                ) : null}
              </div>
              <div className="flex gap-1">
                <IconTooltip
                  icon={{
                    icon: 'user-circle',
                    type: 'regular',
                    style: { color: theme.colors.primary },
                  }}
                >
                  {comment.createdBy
                    ? comment.createdBy.fullName
                    : translations.unknownUser}
                </IconTooltip>
                <IconTooltip
                  icon={{
                    icon: 'clock',
                    type: 'regular',
                    style: { color: theme.colors.primary },
                  }}
                >
                  {format(parseISO(comment.createdAt), 'dd.MM.yyyy HH:mm')}
                </IconTooltip>
              </div>
            </div>
          </td>
          <td className={cn(TD, 'text-right')}>
            <div className="flex items-center justify-end gap-2">
              {canEditSalary &&
                !loading &&
                (!comment.createdBy || comment.createdBy.id === me.id) && (
                  <SalaryCommentDialog
                    comment={comment}
                    isGlobal={isGlobal}
                    userId={filter.user ?? me.id}
                    year={filter.month.getFullYear()}
                    month={filter.month.getMonth() + 1}
                    onSubmit={() => refetch()}
                  />
                )}
              {canEditSalary && (
                <Button
                  size="sm"
                  onClick={() => handleDeleteComment(comment.id)}
                >
                  <FAIcon icon="times" />
                </Button>
              )}
            </div>
          </td>
        </tr>
      )
    }) ?? []

  const floatRows = useMemo(
    () =>
      getScopedSalaryCodeRows(
        data?.monthReport?.groupedActivitySumsFloatAll ?? [],
        data?.monthReport?.groupedActivitySumsFloatApproved ?? [],
        [],
        payrollSumScope
      ).map(activitySum => {
        const lockedValues =
          data?.salaryBaseByMonth?.entriesWithoutDepartment.filter(entry => {
            const local = entry?.salaryId
            const el = activitySum.salaryId
            return local === el
          })
        const lockedValue = lockedValues?.length
          ? lockedValues[0]?.amount ?? 0
          : '0'

        return (
          <tr
            key={`float-${filter.user ?? ''}${activitySum.id}`}
            className={SUM_ROW}
            onMouseEnter={() => setActiveSalaryCode(activitySum.name)}
          >
            <td className={TD_NAME}>{activitySum.name}</td>
            <td className={TD_NUM}>{activitySum.all}</td>
            {hasApprovalFlow && (
              <td className={TD_NUM}>{activitySum.approved ?? 0}</td>
            )}
            <td className={TD_NUM}>
              {data?.monthReport.isLocked && lockedValue}
            </td>
          </tr>
        )
      }),
    [
      data?.monthReport,
      data?.salaryBaseByMonth?.entriesWithoutDepartment,
      filter.user,
      hasApprovalFlow,
      payrollSumScope,
      setActiveSalaryCode,
    ]
  )

  return (
    <ModuleWrapper
      title={
        <span>
          {translations.workOverview}
          <span className="ml-1">
            ({format(startDateCalculation, 'dd. MMM')} -{' '}
            {format(endDateCalculation, 'dd. MMM')})
          </span>
        </span>
      }
      page={PageType.WORK_PERIODS}
      loading={loading}
    >
      {canSeeOthers && canSeeSalaryStatistics && (
        <SalaryStatistics
          year={filter.month.getFullYear()}
          month={filter.month.getMonth() + 1}
          onUserClick={user => updateForm({ user: user.id })}
        />
      )}

      {!popupWorkPeriodGroup &&
        canEditSalary &&
        editedAfterLockUsers.length > 0 && (
          <div className="mb-4">
            <UserWorkPeriodsWarningCard
              users={editedAfterLockUsers}
              title={
                editedAfterLockUsers.length === 1
                  ? translations.oneUserEditedAfterLock
                  : translations.nUsersEditedAfterLock({
                      n: editedAfterLockUsers.length,
                    })
              }
              onSelectUser={selectedUserId =>
                updateForm({ user: selectedUserId })
              }
            />
          </div>
        )}

      {/* Filter bar */}
      <div className="mb-6 flex flex-wrap items-end gap-4 rounded-xl border border-gray-200 bg-white p-4">
        {canSeeOthers && (
          <div className="min-w-[260px] grow sm:grow-0">
            <label className={FILTER_LABEL}>{translations.user}</label>
            <UserCombobox
              mode="single"
              value={filter.user ?? ''}
              onChange={user => updateForm({ user: user || me.id })}
              filterLeaderGroup
              filterSalaryCalculation
              className="h-[3.875rem] min-w-[260px]"
            />
          </div>
        )}

        <div className="min-w-[240px] grow sm:grow-0">
          <label className={FILTER_LABEL}>{translations.month}</label>
          <MonthYearPicker
            value={filter.month}
            futureYears={0}
            onChange={handler('month')}
            className="h-[3.875rem] w-full"
          />
        </div>

        <div>
          <label className={FILTER_LABEL}>{translations.view}</label>
          <div className="inline-flex h-[3.875rem] overflow-hidden rounded-lg border border-gray-200">
            <button
              type="button"
              className={cn(
                'inline-flex h-full items-center gap-2 px-4 text-sm font-medium transition-colors',
                !showList
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              )}
              onClick={() => updateForm({ viewMode: 'calendar' })}
            >
              <FAIcon icon="calendar" />
              {translations.calendar}
            </button>
            <button
              type="button"
              className={cn(
                'inline-flex h-full items-center gap-2 border-l border-gray-200 px-4 text-sm font-medium transition-colors',
                showList
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              )}
              onClick={() => updateForm({ viewMode: 'list' })}
            >
              <FAIcon icon="list" />
              {translations.list}
            </button>
          </div>
        </div>

        {canEditSalary && (
          <div className="ml-auto">
            <label className={FILTER_LABEL}>{translations.salaryBase}</label>
            <div className="flex items-end gap-2">
              <Button
                className="h-[3.875rem]"
                disabled={
                  loading ||
                  userLoading ||
                  filterStorageStateLoading ||
                  lockLoading ||
                  !filter.user ||
                  !data?.monthReport
                }
                onClick={() => void handleLock()}
              >
                {lockLoading ? <Loader.Spinner /> : translations.lock}
              </Button>
              {isAdmin && (
                <Button
                  variant="outline"
                  className="h-[3.875rem]"
                  title={
                    lockRunInProgress
                      ? translations.lockAllInProgress
                      : undefined
                  }
                  disabled={
                    loading ||
                    userLoading ||
                    filterStorageStateLoading ||
                    lockAllLoading ||
                    lockRunInProgress
                  }
                  onClick={() => void handleLockAll()}
                >
                  {lockAllLoading ? (
                    <Loader.Spinner />
                  ) : (
                    <>
                      <FAIcon icon="file-export" />
                      {translations.lockAll}
                    </>
                  )}
                </Button>
              )}
              <Button
                size="icon-lg"
                variant="outline"
                className="h-[3.875rem]"
                title={translations.lockLog}
                aria-label={translations.lockLog}
                onClick={openSalaryLockLog}
              >
                <FAIcon icon="clock-rotate-left" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {!popupWorkPeriodGroup && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_25rem]">
          {/* Left: calendar or list */}
          {showList ? (
            <div className="min-w-0 overflow-x-auto [&>span>div:first-child]:mt-0">
              {isMobileOnly ? (
                <WorkPeriodApproveCards
                  approvalRows={listApprovalRows}
                  leaderGroups={myLeaderGroups}
                  loading={listLoading}
                  initRefetch={refetchListAndReport}
                  refetchAfterApproval={() => void refetch()}
                />
              ) : (
                <WorkPeriodApproveTable
                  approvalRows={listApprovalRows}
                  leaderGroups={myLeaderGroups}
                  showUserColumn={canSeeOthers}
                  loading={listLoading}
                  initRefetch={refetchListAndReport}
                  refetchAfterApproval={() => void refetch()}
                />
              )}
            </div>
          ) : (
            <div className="min-w-0 space-y-3">
              <WorkPeriodCalendar data={calendarData} weeklySums={weeklySums} />
              {usesPlannerForOvertime && <CalendarColorExplanation />}
            </div>
          )}

          {/* Right: sums + corrections + comments + overtime/undertime.
              `contents` below `lg` drops this wrapper so its panels become
              grid items in their own right, letting the salary-code sums move
              above the calendar on a phone -- where the calendar is a long
              scroll that would otherwise bury the month's figures. */}
          <div className="contents lg:flex lg:min-w-0 lg:flex-col lg:gap-4">
            {/* Scrolls sideways rather than clipping when the figure columns
                and a long salary code name cannot share the 25rem column. */}
            <section
              className={cn(PANEL, 'overflow-x-auto max-lg:order-first')}
              onMouseLeave={() => setActiveSalaryCode(null)}
            >
              <table className={SUM_TABLE}>
                <thead className={THEAD}>
                  <tr>
                    <th className={TH}>
                      <span className="inline-flex items-center gap-2">
                        {translations.salaryCode}
                        {canEditSalary && (
                          <button
                            type="button"
                            className="text-gray-400 transition-colors hover:text-gray-600"
                            aria-label={translations.departmentDistribution}
                            title={translations.departmentDistribution}
                            onClick={() => void openSalaryCodeDistribution()}
                          >
                            <FAIcon icon="question-circle" />
                          </button>
                        )}
                      </span>
                    </th>
                    <th className={TH_NUM}>{translations.all}</th>
                    {hasApprovalFlow && (
                      <th className={TH_NUM}>{translations.approved}</th>
                    )}
                    <th className={TH_NUM}>
                      {data?.monthReport.isLocked
                        ? translations.transferredValue
                        : ''}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {timedeltaRows}
                  {floatRows}
                  {data?.monthReport && (
                    <tr className={cn(SUM_ROW, 'bg-gray-50')}>
                      <td className={TD_NAME}>{translations.totalWork}</td>
                      <td className={cn(TD_NUM, 'font-medium')}>
                        {enforceHourMinuteString(
                          data.monthReport.totalWorkTime
                        )}
                      </td>
                      {hasApprovalFlow && (
                        <td className={cn(TD_NUM, 'font-medium')}>
                          {enforceHourMinuteString(
                            data.monthReport.totalWorkTimeApproved
                          )}
                        </td>
                      )}
                      <td className={TD} />
                    </tr>
                  )}
                </tbody>
              </table>
            </section>

            {/* The report always covers exactly one user - the selected one, or
                the viewer themselves - so its corrections are always relevant.
                Users without `view_othersworkperiod` can never set
                `filter.user`, and gating on it hid their own corrections. */}
            <section className={PANEL}>
              <table className={SUM_TABLE}>
                <thead className={THEAD}>
                  <tr>
                    <th className={TH}>{translations.corrections}</th>
                    <th className={TH_NUM}>{translations.amount}</th>
                    <th className={cn(TH, 'text-right')}>
                      {canEditSalary && (
                        <SalaryCorrectionDialog
                          userId={filter.user ?? me.id}
                          year={filter.month.getFullYear()}
                          month={filter.month.getMonth() + 1}
                          onSubmit={() => refetch()}
                        />
                      )}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {correctionRows}
                  {rejectedActivityRows.length > 0 && (
                    <>
                      <tr className={cn(THEAD, 'border-t')}>
                        <th className={TH}>
                          {translations.rejectedActivities}
                        </th>
                        <th className={TH_NUM}>{translations.amount}</th>
                        <th className={TH} />
                      </tr>
                      {rejectedActivityRows}
                      <tr className={cn(SUM_ROW, 'bg-gray-50')}>
                        <td className={TD_NAME}>{translations.sum}</td>
                        <td className={cn(TD_NUM, 'font-medium')}>
                          {formatSecondsAsHourMinute(
                            getRejectedActivitiesTotal(rejectedActivities)
                          )}
                        </td>
                        <td className={TD} />
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </section>

            <Activity mode={filter.user ? 'visible' : 'hidden'}>
              <section className={PANEL}>
                <table className={SUM_TABLE}>
                  <thead className={THEAD}>
                    <tr>
                      <th className={TH} colSpan={2}>
                        {translations.comments}
                      </th>
                      <th className={cn(TH, 'text-right')}>
                        {canEditSalary && (
                          <SalaryCommentDialog
                            userId={filter.user ?? me.id}
                            year={filter.month.getFullYear()}
                            month={filter.month.getMonth() + 1}
                            onSubmit={() => refetch()}
                          />
                        )}
                      </th>
                    </tr>
                  </thead>
                  <tbody>{commentRows}</tbody>
                </table>
              </section>
            </Activity>

            {(data?.monthReport?.overtime?.length ?? 0) > 0 && (
              <section className={PANEL}>
                <table className={SUM_TABLE}>
                  <thead className={THEAD}>
                    <tr>
                      <th className={TH}>{translations.overtime}</th>
                      <th className={TH_NUM}>{translations.amount}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.monthReport?.overtime?.map((activitySum, i) => (
                      <tr key={`overtime-sums-${i}`} className={SUM_ROW}>
                        <td className={TD_NAME}>
                          {activitySum.name}
                          {activitySum.nextMonth && '*'}
                        </td>
                        <td className={TD_NUM}>
                          {enforceHourMinuteString(activitySum?.sum)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            )}

            {!loading && hasOverlappingMonths && overtimeText}

            {(data?.monthReport?.undertime?.length ?? 0) > 0 && (
              <section className={PANEL}>
                <table className={SUM_TABLE}>
                  <thead className={THEAD}>
                    <tr>
                      <th className={TH}>{translations.undertime}</th>
                      <th className={TH_NUM}>{translations.amount}</th>
                      <th className={TH} />
                    </tr>
                  </thead>
                  <tbody>
                    {data?.monthReport?.undertime?.map(activitySum => (
                      <tr
                        key={`undertime-${activitySum.id}`}
                        className={SUM_ROW}
                      >
                        <td className={TD_NAME}>
                          {activitySum.name}
                          {activitySum.nextMonth && '*'}
                        </td>
                        <td className={TD_NUM}>
                          {enforceHourMinuteString(activitySum?.sum)}
                        </td>
                        <td className={cn(TD, 'text-right')}>
                          {canEditSalary && !loading && (
                            <SalaryCorrectionDialog
                              userId={filter.user ?? me.id}
                              year={filter.month.getFullYear()}
                              month={filter.month.getMonth() + 1}
                              prefill={{
                                amount: timeDeltaToDecimalHours(
                                  activitySum.sum
                                ),
                              }}
                              trigger={
                                <Button
                                  size="sm"
                                  className="h-8 w-8 p-0"
                                  title={translations.addSlashCorrect}
                                  aria-label={translations.addSlashCorrect}
                                >
                                  <FAIcon icon="plus" />
                                </Button>
                              }
                              onSubmit={() => refetch()}
                            />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            )}
          </div>
        </div>
      )}

      <div>
        {popupWorkPeriodGroup &&
          (!usesPlannerForOvertime && popupWorkPeriodGroup.workPeriods[0] ? (
            <WorkPeriodDetails
              workPeriod={popupWorkPeriodGroup.workPeriods[0]}
              userId={filter.user ?? me.id}
              canEdit={canEditSalary}
              patchWorkPeriod={(workPeriodId, values) =>
                handlePatchWorkPeriod(workPeriodId, values)
              }
              onClose={handleExitDetails}
              salaryCalendarSettings={salaryConfiguration}
              refetch={() => refetch()}
            />
          ) : popupWorkPeriodGroup.absenceDay ? (
            popupWorkPeriodGroup.absenceDay.absence && (
              <AbsenceDetailsSalary
                absenceId={popupWorkPeriodGroup.absenceDay.absence.id}
                onClose={handleExitDetails}
              />
            )
          ) : (
            <WorkPeriodGroupDetails
              onAttachModalClose={() => {}}
              group={popupWorkPeriodGroup}
              userId={filter.user ?? me.id}
              salaryCalendarSettings={salaryConfiguration}
              canEdit={canEditSalary}
              patchWorkPeriod={(workPeriodId, values) =>
                handlePatchWorkPeriod(workPeriodId, values)
              }
              onClose={handleExitDetails}
              refetch={() => refetch()}
            />
          ))}
      </div>
      {!popupWorkPeriodGroup && (
        <>
          <AddonOverview
            userId={filter.user ?? me.id}
            datetimeStart={startDateCalculation}
            datetimeEnd={endDateCalculation}
          />
          <ApprovalCommentsList workPeriods={workPeriodsInScope ?? []} />
        </>
      )}
    </ModuleWrapper>
  )
}
